import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';

// Import availability API functions
import {
  getMyCalendarAvailability,
  createMyCalendarAvailabilityBlock,
  updateCalendarAvailabilityBlock,
  deleteCalendarAvailabilityBlock,
  getMyWeeklyScheduleForForm,
  updateMyWeeklySchedule,
  getPetDataForCustomers,
} from '../../util/api';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_REQUEST = 'app/ProviderDashboardPage/FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_REQUEST';
export const FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_SUCCESS = 'app/ProviderDashboardPage/FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_SUCCESS';
export const FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_ERROR = 'app/ProviderDashboardPage/FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_ERROR';

export const FETCH_DASHBOARD_SALES_REQUEST = 'app/ProviderDashboardPage/FETCH_DASHBOARD_SALES_REQUEST';
export const FETCH_DASHBOARD_SALES_SUCCESS = 'app/ProviderDashboardPage/FETCH_DASHBOARD_SALES_SUCCESS';
export const FETCH_DASHBOARD_SALES_ERROR = 'app/ProviderDashboardPage/FETCH_DASHBOARD_SALES_ERROR';

// Availability action types
export const FETCH_DASHBOARD_AVAILABILITY_REQUEST = 'app/ProviderDashboardPage/FETCH_DASHBOARD_AVAILABILITY_REQUEST';
export const FETCH_DASHBOARD_AVAILABILITY_SUCCESS = 'app/ProviderDashboardPage/FETCH_DASHBOARD_AVAILABILITY_SUCCESS';
export const FETCH_DASHBOARD_AVAILABILITY_ERROR = 'app/ProviderDashboardPage/FETCH_DASHBOARD_AVAILABILITY_ERROR';

export const CREATE_DASHBOARD_AVAILABILITY_REQUEST = 'app/ProviderDashboardPage/CREATE_DASHBOARD_AVAILABILITY_REQUEST';
export const CREATE_DASHBOARD_AVAILABILITY_SUCCESS = 'app/ProviderDashboardPage/CREATE_DASHBOARD_AVAILABILITY_SUCCESS';
export const CREATE_DASHBOARD_AVAILABILITY_ERROR = 'app/ProviderDashboardPage/CREATE_DASHBOARD_AVAILABILITY_ERROR';

export const UPDATE_DASHBOARD_AVAILABILITY_REQUEST = 'app/ProviderDashboardPage/UPDATE_DASHBOARD_AVAILABILITY_REQUEST';
export const UPDATE_DASHBOARD_AVAILABILITY_SUCCESS = 'app/ProviderDashboardPage/UPDATE_DASHBOARD_AVAILABILITY_SUCCESS';
export const UPDATE_DASHBOARD_AVAILABILITY_ERROR = 'app/ProviderDashboardPage/UPDATE_DASHBOARD_AVAILABILITY_ERROR';

export const DELETE_DASHBOARD_AVAILABILITY_REQUEST = 'app/ProviderDashboardPage/DELETE_DASHBOARD_AVAILABILITY_REQUEST';
export const DELETE_DASHBOARD_AVAILABILITY_SUCCESS = 'app/ProviderDashboardPage/DELETE_DASHBOARD_AVAILABILITY_SUCCESS';
export const DELETE_DASHBOARD_AVAILABILITY_ERROR = 'app/ProviderDashboardPage/DELETE_DASHBOARD_AVAILABILITY_ERROR';

export const FETCH_WEEKLY_SCHEDULE_REQUEST = 'app/ProviderDashboardPage/FETCH_WEEKLY_SCHEDULE_REQUEST';
export const FETCH_WEEKLY_SCHEDULE_SUCCESS = 'app/ProviderDashboardPage/FETCH_WEEKLY_SCHEDULE_SUCCESS';
export const FETCH_WEEKLY_SCHEDULE_ERROR = 'app/ProviderDashboardPage/FETCH_WEEKLY_SCHEDULE_ERROR';

export const UPDATE_WEEKLY_SCHEDULE_REQUEST = 'app/ProviderDashboardPage/UPDATE_WEEKLY_SCHEDULE_REQUEST';
export const UPDATE_WEEKLY_SCHEDULE_SUCCESS = 'app/ProviderDashboardPage/UPDATE_WEEKLY_SCHEDULE_SUCCESS';
export const UPDATE_WEEKLY_SCHEDULE_ERROR = 'app/ProviderDashboardPage/UPDATE_WEEKLY_SCHEDULE_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  // Calendar data
  fetchCalendarTransactionsInProgress: false,
  fetchCalendarTransactionsError: null,
  calendarTransactions: [],
  calendarPagination: null,
  
  // Inbox data
  fetchSalesInProgress: false,
  fetchSalesError: null,
  salesTransactionRefs: [],
  salesPagination: null,

  // Availability data
  fetchAvailabilityInProgress: false,
  fetchAvailabilityError: null,
  availabilityData: null,
  createAvailabilityInProgress: false,
  createAvailabilityError: null,
  updateAvailabilityInProgress: false,
  updateAvailabilityError: null,
  deleteAvailabilityInProgress: false,
  deleteAvailabilityError: null,

  // Weekly schedule data
  fetchWeeklyScheduleInProgress: false,
  fetchWeeklyScheduleError: null,
  weeklyScheduleData: null,
  updateWeeklyScheduleInProgress: false,
  updateWeeklyScheduleError: null,
};

export default function providerDashboardPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  
  switch (type) {
    // Calendar actions
    case FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_REQUEST:
      return { ...state, fetchCalendarTransactionsInProgress: true, fetchCalendarTransactionsError: null };
    case FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_SUCCESS:
      const { entities, meta } = payload;
      return {
        ...state,
        fetchCalendarTransactionsInProgress: false,
        calendarTransactions: entities || [],
        calendarPagination: meta || null,
      };
    case FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchCalendarTransactionsInProgress: false, fetchCalendarTransactionsError: payload };

    // Inbox actions
    case FETCH_DASHBOARD_SALES_REQUEST:
      return { ...state, fetchSalesInProgress: true, fetchSalesError: null };
    case FETCH_DASHBOARD_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchSalesInProgress: false,
        salesTransactionRefs: entityRefs(transactions),
        salesPagination: payload.data.meta,
      };
    }
    case FETCH_DASHBOARD_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchSalesInProgress: false, fetchSalesError: payload };

    // Availability actions
    case FETCH_DASHBOARD_AVAILABILITY_REQUEST:
      return { ...state, fetchAvailabilityInProgress: true, fetchAvailabilityError: null };
    case FETCH_DASHBOARD_AVAILABILITY_SUCCESS:
      return {
        ...state,
        fetchAvailabilityInProgress: false,
        availabilityData: payload.data,
      };
    case FETCH_DASHBOARD_AVAILABILITY_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchAvailabilityInProgress: false, fetchAvailabilityError: payload };

    case CREATE_DASHBOARD_AVAILABILITY_REQUEST:
      return { ...state, createAvailabilityInProgress: true, createAvailabilityError: null };
    case CREATE_DASHBOARD_AVAILABILITY_SUCCESS:
      return { ...state, createAvailabilityInProgress: false };
    case CREATE_DASHBOARD_AVAILABILITY_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, createAvailabilityInProgress: false, createAvailabilityError: payload };

    case UPDATE_DASHBOARD_AVAILABILITY_REQUEST:
      return { ...state, updateAvailabilityInProgress: true, updateAvailabilityError: null };
    case UPDATE_DASHBOARD_AVAILABILITY_SUCCESS:
      return { ...state, updateAvailabilityInProgress: false };
    case UPDATE_DASHBOARD_AVAILABILITY_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, updateAvailabilityInProgress: false, updateAvailabilityError: payload };

    case DELETE_DASHBOARD_AVAILABILITY_REQUEST:
      return { ...state, deleteAvailabilityInProgress: true, deleteAvailabilityError: null };
    case DELETE_DASHBOARD_AVAILABILITY_SUCCESS:
      return { ...state, deleteAvailabilityInProgress: false };
    case DELETE_DASHBOARD_AVAILABILITY_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, deleteAvailabilityInProgress: false, deleteAvailabilityError: payload };

    // Weekly schedule actions
    case FETCH_WEEKLY_SCHEDULE_REQUEST:
      return { ...state, fetchWeeklyScheduleInProgress: true, fetchWeeklyScheduleError: null };
    case FETCH_WEEKLY_SCHEDULE_SUCCESS:
      return {
        ...state,
        fetchWeeklyScheduleInProgress: false,
        weeklyScheduleData: payload.data,
      };
    case FETCH_WEEKLY_SCHEDULE_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchWeeklyScheduleInProgress: false, fetchWeeklyScheduleError: payload };

    case UPDATE_WEEKLY_SCHEDULE_REQUEST:
      return { ...state, updateWeeklyScheduleInProgress: true, updateWeeklyScheduleError: null };
    case UPDATE_WEEKLY_SCHEDULE_SUCCESS:
      return {
        ...state,
        updateWeeklyScheduleInProgress: false,
        weeklyScheduleData: payload.data,
      };
    case UPDATE_WEEKLY_SCHEDULE_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, updateWeeklyScheduleInProgress: false, updateWeeklyScheduleError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

// Calendar action creators
const fetchDashboardCalendarTransactionsRequest = () => ({ type: FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_REQUEST });
const fetchDashboardCalendarTransactionsSuccess = response => ({
  type: FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_SUCCESS,
  payload: response,
});
const fetchDashboardCalendarTransactionsError = e => ({
  type: FETCH_DASHBOARD_CALENDAR_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

// Inbox action creators
const fetchDashboardSalesRequest = () => ({ type: FETCH_DASHBOARD_SALES_REQUEST });
const fetchDashboardSalesSuccess = response => ({
  type: FETCH_DASHBOARD_SALES_SUCCESS,
  payload: response,
});
const fetchDashboardSalesError = e => ({
  type: FETCH_DASHBOARD_SALES_ERROR,
  error: true,
  payload: e,
});

// Availability action creators
const fetchDashboardAvailabilityRequest = () => ({ type: FETCH_DASHBOARD_AVAILABILITY_REQUEST });
const fetchDashboardAvailabilitySuccess = response => ({
  type: FETCH_DASHBOARD_AVAILABILITY_SUCCESS,
  payload: response,
});
const fetchDashboardAvailabilityError = e => ({
  type: FETCH_DASHBOARD_AVAILABILITY_ERROR,
  error: true,
  payload: e,
});

const createDashboardAvailabilityRequest = () => ({ type: CREATE_DASHBOARD_AVAILABILITY_REQUEST });
const createDashboardAvailabilitySuccess = response => ({
  type: CREATE_DASHBOARD_AVAILABILITY_SUCCESS,
  payload: response,
});
const createDashboardAvailabilityError = e => ({
  type: CREATE_DASHBOARD_AVAILABILITY_ERROR,
  error: true,
  payload: e,
});

const updateDashboardAvailabilityRequest = () => ({ type: UPDATE_DASHBOARD_AVAILABILITY_REQUEST });
const updateDashboardAvailabilitySuccess = response => ({
  type: UPDATE_DASHBOARD_AVAILABILITY_SUCCESS,
  payload: response,
});
const updateDashboardAvailabilityError = e => ({
  type: UPDATE_DASHBOARD_AVAILABILITY_ERROR,
  error: true,
  payload: e,
});

const deleteDashboardAvailabilityRequest = () => ({ type: DELETE_DASHBOARD_AVAILABILITY_REQUEST });
const deleteDashboardAvailabilitySuccess = response => ({
  type: DELETE_DASHBOARD_AVAILABILITY_SUCCESS,
  payload: response,
});
const deleteDashboardAvailabilityError = e => ({
  type: DELETE_DASHBOARD_AVAILABILITY_ERROR,
  error: true,
  payload: e,
});

// Weekly schedule action creators
const fetchWeeklyScheduleRequest = () => ({ type: FETCH_WEEKLY_SCHEDULE_REQUEST });
const fetchWeeklyScheduleSuccess = response => ({
  type: FETCH_WEEKLY_SCHEDULE_SUCCESS,
  payload: response,
});
const fetchWeeklyScheduleError = e => ({
  type: FETCH_WEEKLY_SCHEDULE_ERROR,
  error: true,
  payload: e,
});

const updateWeeklyScheduleRequest = () => ({ type: UPDATE_WEEKLY_SCHEDULE_REQUEST });
const updateWeeklyScheduleSuccess = response => ({
  type: UPDATE_WEEKLY_SCHEDULE_SUCCESS,
  payload: response,
});
const updateWeeklyScheduleError = e => ({
  type: UPDATE_WEEKLY_SCHEDULE_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const LEADS_PAGE_SIZE = 100; // Larger page size for calendar view
const SALES_PAGE_SIZE = 20; // Smaller page size for dashboard inbox

// Fetch leads transactions for calendar
export const fetchDashboardLeads = (params = {}) => (dispatch, getState, sdk) => {
  dispatch(fetchDashboardCalendarTransactionsRequest());
  
  const { currentUser } = getState().user;
  
  if (!currentUser) {
    const error = new Error('User not authenticated');
    dispatch(fetchDashboardCalendarTransactionsError(storableError(error)));
    return Promise.reject(error);
  }

  // Query parameters for fetching transactions where current user is the provider
  const queryParams = {
    only: 'sale', // 'sale' means current user is the provider
    processNames: ['request-lead','default-booking'],
    include: ['customer', 'provider', 'listing', 'booking'],
    'fields.transaction': [
      'id',
      'createdAt',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'processName',
      'processVersion',
      'protectedData.leadStart',
      'protectedData.leadEnd',
      'protectedData.notes',
      'protectedData.leadTransactionId',
      'protectedData.bookingTransactionId',
    ],
    'fields.user': ['id', 'profile'],
    'fields.listing': ['id', 'title'],
    perPage: LEADS_PAGE_SIZE,
    ...params
  };

  return sdk.transactions
    .query(queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      const meta = response.data.meta;
      
      dispatch(fetchDashboardCalendarTransactionsSuccess({
        entities,
        meta
      }));
      return response;
    })
    .catch(e => {
      dispatch(fetchDashboardCalendarTransactionsError(storableError(e)));
      throw e;
    });
};

// Fetch sales transactions for inbox
export const fetchDashboardSales = (params = {}) => (dispatch, getState, sdk) => {
  dispatch(fetchDashboardSalesRequest());

  const { page = 1 } = params;

  const apiQueryParams = {
    only: 'sale', // Provider transactions only
    processNames: ['request-lead'],
    // lastTransitions: getAllTransitionsForEveryProcess(),
    lastTransitions: [
      'transition/request',
      'transition/accept',
      'transition/initiate-booking',
      'transition/confirm-booking',
    ],
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: SALES_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(async (response) => {
      const transactions_to_filter = [];
      // console.log('Fetched sales transactions:', response?.data?.data);
      const promise_array = [];
      // retrieve bookingTransactionId from protectedData for each transaction
      response?.data?.data?.forEach(tx => {
        const protectedData = tx?.attributes?.protectedData;
        const {
          bookingTransactionId,
        } = protectedData || {};
        if (bookingTransactionId) {
          promise_array.push(sdk.transactions.show({ id: bookingTransactionId }).then((r) => {
            const {
              lastTransition,
            } = r?.data?.data?.attributes || {};
            if ([
              'transition/expire-review-period',
              'transition/cancel',
              'transition/operator-decline',
              'transition/cancel-by-customer',
              'transition/provider-decline',
            ].includes(lastTransition)) {
              transactions_to_filter.push(tx.id?.uuid);
            }
          }));
        }
      });
      await Promise.all(promise_array);
      const filtered_response = { ...response };
      filtered_response.data.data = response?.data?.data?.filter(tx => !transactions_to_filter.includes(tx?.id?.uuid));
      // console.log('Filtered sales transactions:', filtered_response?.data?.data);
      return filtered_response;
    })
    .then(async (response) => {
      const unique_customer_ids = [];
      response?.data?.data?.forEach(tx => {
        const customer_id = tx?.relationships?.customer?.data?.id?.uuid;
        if (customer_id && !unique_customer_ids.includes(customer_id)) {
          unique_customer_ids.push(customer_id);
        }
      });
      const petData = await getPetDataForCustomers(unique_customer_ids);
      response?.data?.data?.forEach((tx, index) => {
        const customer_id = tx?.relationships?.customer?.data?.id?.uuid;
        if (
          customer_id
          && petData?.petData?.[customer_id]
        ) {
          response.data.data[index].attributes.protectedData.petData = petData.petData[customer_id];
        }
      });
      // console.log('Fetched sales transactions:', response?.data?.data);
      // console.log('Fetched pet data for customers:', petData);
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchDashboardSalesSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchDashboardSalesError(storableError(e)));
      throw e;
    });
};

// Fetch availability data for calendar
export const fetchDashboardAvailability = (startDate, endDate) => (dispatch, getState, sdk) => {
  dispatch(fetchDashboardAvailabilityRequest());

  return getMyCalendarAvailability(startDate, endDate)
    .then(response => {
      const transformedData = {
        total_items: response?.total_items || 0,
        items: Array.isArray(response?.items) ? response.items.map(mi => ({
          ...mi,
          start: mi.start_datetime,
          end: mi.end_datetime,
          type: mi.availability_type,
          description: mi.reason,
        })) : [],
      };
      dispatch(fetchDashboardAvailabilitySuccess({ data: transformedData }));
      return response;
    })
    .catch(e => {
      dispatch(fetchDashboardAvailabilityError(storableError(e)));
      throw e;
    });
};

// Create availability event
export const createDashboardAvailabilityEvent = (params) => (dispatch, getState, sdk) => {
  dispatch(createDashboardAvailabilityRequest());

  return createMyCalendarAvailabilityBlock(params)
    .then(response => {
      dispatch(createDashboardAvailabilitySuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(createDashboardAvailabilityError(storableError(e)));
      throw e;
    });
};

// Update availability event
export const updateDashboardAvailabilityEvent = (blockId, params) => (dispatch, getState, sdk) => {
  dispatch(updateDashboardAvailabilityRequest());

  return updateCalendarAvailabilityBlock(blockId, params)
    .then(response => {
      dispatch(updateDashboardAvailabilitySuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(updateDashboardAvailabilityError(storableError(e)));
      throw e;
    });
};

// Delete availability event
export const deleteDashboardAvailabilityEvent = (blockId) => (dispatch, getState, sdk) => {
  dispatch(deleteDashboardAvailabilityRequest());

  return deleteCalendarAvailabilityBlock(blockId)
    .then(response => {
      dispatch(deleteDashboardAvailabilitySuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(deleteDashboardAvailabilityError(storableError(e)));
      throw e;
    });
};

// Fetch weekly schedule
export const fetchDashboardWeeklySchedule = () => (dispatch, getState, sdk) => {
  dispatch(fetchWeeklyScheduleRequest());

  return getMyWeeklyScheduleForForm()
    .then(response => {
      dispatch(fetchWeeklyScheduleSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchWeeklyScheduleError(storableError(e)));
      throw e;
    });
};

// Update weekly schedule
export const updateDashboardWeeklySchedule = (scheduleData) => (dispatch, getState, sdk) => {
  dispatch(updateWeeklyScheduleRequest());

  return updateMyWeeklySchedule(scheduleData)
    .then(response => {
      dispatch(updateWeeklyScheduleSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(updateWeeklyScheduleError(storableError(e)));
      throw e;
    });
};

// Load all dashboard data including availability
export const loadData = (params = {}) => dispatch => {
  const currentMonth = new Date();
  const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
    .toISOString().split('T')[0];
  const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 24, 0)
    .toISOString().split('T')[0];

  return Promise.all([
    dispatch(fetchDashboardLeads(params)),
    dispatch(fetchDashboardSales(params)),
    dispatch(fetchDashboardAvailability(startDate, endDate)),
    dispatch(fetchDashboardWeeklySchedule())
  ]);
};